import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { ApiError } from '../api/client'
import { SURFACE_LABELS, type PublishSurface } from '../api/media'
import { TikTokPublishDialog } from '../components/TikTokPublishDialog'
import {
  ALL_SURFACES,
  composeCaption,
  contentApi,
  contentErrorText,
  draftStatus,
  hasBlockingIssue,
  usesTitle,
  type ContentIdeaSummary,
  type GeneratedContentSummary,
} from '../api/content'

type Banner = { kind: 'success' | 'error' | 'warn'; text: string }

/**
 * Servern äger felkoderna; här översätts de bara. En tyst standardsträng vore
 * fel — en okänd kod ska synas som en okänd kod, inte som "något gick fel".
 */
function describeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string } | null
    return (
      contentErrorText(body?.error, err.status) ??
      body?.error ??
      `${fallback} (HTTP ${err.status})`
    )
  }
  return fallback
}

export function ContentPage() {
  const [ideas, setIdeas] = useState<ContentIdeaSummary[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [banner, setBanner] = useState<Banner | null>(null)

  // Kvarstående anmärkningar finns BARA i 201-svaret från /generate. En senare
  // GET bär dem inte (ToSummary sätter tom lista). Fångas de inte här är de borta.
  const [unresolved, setUnresolved] = useState<string[]>([])

  const [description, setDescription] = useState('')
  const [goal, setGoal] = useState('')
  const [targetAudience, setTargetAudience] = useState('')
  const [tone, setTone] = useState('')
  const [referenceMaterial, setReferenceMaterial] = useState('')
  const [surfaces, setSurfaces] = useState<Set<PublishSurface>>(new Set(ALL_SURFACES))

  const [errors, setErrors] = useState<{ description?: boolean; surfaces?: boolean }>({})
  const [busy, setBusy] = useState(false)

  // Skärmläsarens enda kanal för att veta att något pågår. Elementet ligger i DOM
  // från mount och är TOMT — en live-region som villkorsrenderas annonseras inte
  // pålitligt, eftersom hjälpmedlet aldrig observerade noden när den skapades.
  const [statusMessage, setStatusMessage] = useState('')

  const descriptionRef = useRef<HTMLTextAreaElement>(null)
  const firstSurfaceRef = useRef<HTMLInputElement>(null)
  const resultsHeadingRef = useRef<HTMLHeadingElement>(null)
  const [focusToken, setFocusToken] = useState(0)

  const load = useCallback(async () => {
    try {
      setIdeas(await contentApi.listIdeas())
    } catch {
      setBanner({ kind: 'error', text: 'Kunde inte hämta tidigare idéer.' })
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  // Fokus flyttas till resultatrubriken när utkasten dyker upp. WCAG KRÄVER inte
  // detta — 4.1.3 uppfylls redan av live-regionen ovan — men efter 45 sekunder är
  // fokus någon helt annanstans, och flytten är en förväntad följd av ägarens eget
  // klick. Rubriken, inte första textarean: den senare får skärmläsaren att börja
  // läsa upp hela captionen.
  useEffect(() => {
    if (focusToken > 0) resultsHeadingRef.current?.focus()
  }, [focusToken])

  const selected = ideas.find((i) => i.id === selectedId) ?? null

  function toggleSurface(surface: PublishSurface) {
    setSurfaces((prev) => {
      const next = new Set(prev)
      if (next.has(surface)) next.delete(surface)
      else next.add(surface)
      return next
    })
  }

  async function onGenerate(e: FormEvent) {
    e.preventDefault()
    if (busy) return

    const next = {
      description: description.trim().length === 0,
      surfaces: surfaces.size === 0,
    }
    setErrors(next)

    // Fokus till första ogiltiga fältet. Då läser skärmläsaren fältets etikett
    // plus dess aria-describedby-fel — ett meddelande i taget, i stället för fem
    // samtidiga role="alert" som talar i mun på varandra.
    if (next.description) return descriptionRef.current?.focus()
    if (next.surfaces) return firstSurfaceRef.current?.focus()

    setBanner(null)
    setUnresolved([])
    setBusy(true)
    setStatusMessage('Genererar utkast. Det tar mellan 20 och 90 sekunder.')

    try {
      const idea = await contentApi.generate({
        description: description.trim(),
        goal: goal.trim(),
        targetAudience: targetAudience.trim(),
        tone: tone.trim(),
        referenceMaterial: referenceMaterial.trim(),
        surfaces: [...surfaces],
      })
      setIdeas((prev) => [idea, ...prev])
      setSelectedId(idea.id)
      setUnresolved(idea.unresolvedProblems)
      setStatusMessage(`${idea.drafts.length} utkast klara.`)
      setFocusToken((t) => t + 1)
    } catch (err) {
      setStatusMessage('')
      setBanner({
        kind: 'error',
        text: describeError(err, 'Genereringen misslyckades.'),
      })
    } finally {
      setBusy(false)
    }
  }

  async function onDeleteIdea(idea: ContentIdeaSummary) {
    if (!window.confirm('Ta bort idén och alla dess utkast permanent?')) return
    try {
      await contentApi.deleteIdea(idea.id)
      setIdeas((prev) => prev.filter((i) => i.id !== idea.id))
      if (selectedId === idea.id) setSelectedId(null)
      setBanner({ kind: 'success', text: 'Idén borttagen.' })
    } catch (err) {
      setBanner({ kind: 'error', text: describeError(err, 'Kunde inte ta bort idén.') })
    }
  }

  function onDraftSaved(updated: GeneratedContentSummary) {
    setIdeas((prev) =>
      prev.map((idea) =>
        idea.id !== selectedId
          ? idea
          : { ...idea, drafts: idea.drafts.map((d) => (d.id === updated.id ? updated : d)) },
      ),
    )
  }

  return (
    <main className="page">
      <h1>Innehåll</h1>
      <p className="muted">
        Beskriv en idé. Claude skriver ett utkast per publiceringsyta. Ingenting
        publiceras — du redigerar och godkänner själv.
      </p>

      {/* Persistent, tom vid mount. Byts bara texten, aldrig elementet. */}
      <p role="status" className="sr-only">
        {statusMessage}
      </p>

      {banner && (
        <div role="status" className={`banner banner-${banner.kind}`}>
          {banner.text}
        </div>
      )}

      <section className="card">
        <h2>Ny idé</h2>

        <form onSubmit={(e) => void onGenerate(e)} noValidate>
          <div className="field">
            <label htmlFor="gen-description">
              Beskrivning <span className="muted small">(obligatorisk)</span>
            </label>
            <p id="gen-description-hint" className="muted small">
              Vad ska inlägget handla om? En eller två meningar räcker.
            </p>
            <textarea
              id="gen-description"
              ref={descriptionRef}
              required
              rows={3}
              value={description}
              aria-invalid={!!errors.description}
              aria-describedby={
                errors.description
                  ? 'gen-description-hint gen-description-error'
                  : 'gen-description-hint'
              }
              onChange={(e) => setDescription(e.target.value)}
            />
            {errors.description && (
              <p id="gen-description-error" className="field-error">
                Skriv en beskrivning innan du genererar.
              </p>
            )}
          </div>

          <div className="field">
            <label htmlFor="gen-goal">Mål</label>
            <input
              id="gen-goal"
              type="text"
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="gen-audience">Målgrupp</label>
            <input
              id="gen-audience"
              type="text"
              value={targetAudience}
              onChange={(e) => setTargetAudience(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="gen-tone">Tonfall</label>
            <input
              id="gen-tone"
              type="text"
              value={tone}
              onChange={(e) => setTone(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="gen-reference">Referensmaterial</label>
            <p id="gen-reference-hint" className="muted small">
              Klistrad text behandlas som data, aldrig som instruktioner till modellen.
            </p>
            <textarea
              id="gen-reference"
              rows={3}
              value={referenceMaterial}
              aria-describedby="gen-reference-hint"
              onChange={(e) => setReferenceMaterial(e.target.value)}
            />
          </div>

          {/*
            fieldset + legend, inte en rubrik ovanför lösa rutor. WCAG 1.3.1 kräver
            att en gruppering som syns visuellt också är programmatiskt bestämbar.
            Felet hängs på fieldset via aria-describedby — aria-invalid har svagt
            stöd på just det elementet.
          */}
          <fieldset
            className="field surfaces"
            aria-describedby={errors.surfaces ? 'gen-surfaces-error' : undefined}
          >
            <legend>Publiceringsytor</legend>
            <p className="muted small">Välj minst en. Alla är förvalda.</p>

            {ALL_SURFACES.map((surface, index) => (
              <label key={surface} className="checkbox-row">
                <input
                  type="checkbox"
                  ref={index === 0 ? firstSurfaceRef : undefined}
                  checked={surfaces.has(surface)}
                  onChange={() => toggleSurface(surface)}
                />
                <span>{SURFACE_LABELS[surface]}</span>
              </label>
            ))}

            {errors.surfaces && (
              <p id="gen-surfaces-error" className="field-error">
                Välj minst en publiceringsyta.
              </p>
            )}
          </fieldset>

          {/*
            aria-disabled i stället för disabled: en disabled knapp tappar fokus,
            och att kasta bort fokus i 45–90 sekunder är sämre än att låta ett andra
            klick falla på `if (busy) return` överst i onGenerate.
          */}
          <button type="submit" className="primary" aria-disabled={busy}>
            {busy ? 'Genererar…' : 'Generera utkast'}
          </button>
        </form>
      </section>

      {/* aria-busy talar aldrig själv — den säger bara "läs inte upp halvbyggd DOM". */}
      <section className="card" aria-busy={busy}>
        <h2 tabIndex={-1} ref={resultsHeadingRef}>
          Utkast
        </h2>

        {unresolved.length > 0 && (
          <div role="status" className="banner banner-warn">
            <p>Utkasten sparades trots anmärkningar från genereringen:</p>
            <ul>
              {unresolved.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          </div>
        )}

        {!selected ? (
          <p className="muted small">
            {busy
              ? 'Genererar utkast…'
              : 'Välj en idé nedan, eller skapa en ny för att se utkast här.'}
          </p>
        ) : (
          selected.drafts.map((draft) => (
            <DraftCard
              key={draft.id}
              draft={draft}
              onSaved={onDraftSaved}
              onError={setBanner}
              onPublished={() => void load()}
            />
          ))
        )}
      </section>

      <section className="card">
        <h2>Idéer</h2>

        {ideas.length === 0 ? (
          <p className="muted small no-accounts">Inga idéer än. Skriv din första ovan.</p>
        ) : (
          <ul className="idea-list">
            {ideas.map((idea) => (
              <li key={idea.id} className="idea-row">
                <div className="idea-meta">
                  <p className="idea-description">{idea.description}</p>
                  <p className="muted small">
                    {new Date(idea.createdAt).toLocaleString('sv-SE')} ·{' '}
                    {idea.drafts.length} utkast
                  </p>
                </div>
                <button
                  type="button"
                  className="link idea-action"
                  aria-pressed={selectedId === idea.id}
                  onClick={() => setSelectedId(idea.id)}
                >
                  {selectedId === idea.id ? 'Visas' : 'Visa utkast'}
                </button>
                <button
                  type="button"
                  className="link idea-action"
                  onClick={() => void onDeleteIdea(idea)}
                >
                  Ta bort
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  )
}

/**
 * Ett utkast, redigerbart. Anmärkningarna kommer från servern och räknas om vid
 * varje sparning — de gäller alltså den SENAST SPARADE texten, inte den du just
 * skriver. Det står i UI:t; att låtsas annat vore att visa en grön bock för text
 * ingen validerat.
 */
function DraftCard({
  draft,
  onSaved,
  onError,
  onPublished,
}: {
  draft: GeneratedContentSummary
  onSaved: (updated: GeneratedContentSummary) => void
  onError: (banner: Banner) => void
  onPublished: () => void
}) {
  const [title, setTitle] = useState(draft.title)
  const [body, setBody] = useState(draft.body)
  const [hashtags, setHashtags] = useState(draft.hashtags)
  const [callToAction, setCallToAction] = useState(draft.callToAction)
  const [saving, setSaving] = useState(false)
  const [publishing, setPublishing] = useState(false)

  const dirty =
    title !== draft.title ||
    body !== draft.body ||
    hashtags !== draft.hashtags ||
    callToAction !== draft.callToAction

  const used = composeCaption(body, callToAction, hashtags).length
  const max = draft.captionMaxChars
  const over = used > max

  // Två sanningskällor med olika räckvidd, och de får inte blandas ihop.
  // `over` vet vi live vid varje tangenttryck. Serverns anmärkningar gäller den
  // SENAST SPARADE texten — de säger ingenting om det man just skrivit.
  //
  // Alltså: så länge fältet är orört bär det serverns dom. I samma stund ägaren
  // börjar skriva faller vi tillbaka på det enda vi kan räkna ut själva. Annars
  // skulle en textarea som ägaren just rättat fortsätta påstå att den är ogiltig.
  const invalid = over || (!dirty && hasBlockingIssue(draft))

  const status = draftStatus(draft)
  const editable = draft.status === 'Draft'
  const id = `draft-${draft.id}`

  function reset() {
    setTitle(draft.title)
    setBody(draft.body)
    setHashtags(draft.hashtags)
    setCallToAction(draft.callToAction)
  }

  async function save() {
    if (saving) return
    setSaving(true)
    try {
      onSaved(await contentApi.updateDraft(draft.id, { title, body, hashtags, callToAction }))
    } catch (err) {
      onError({ kind: 'error', text: describeError(err, 'Kunde inte spara utkastet.') })
    } finally {
      setSaving(false)
    }
  }

  return (
    <article className="draft-card">
      <div className="platform-head">
        <h3>{SURFACE_LABELS[draft.surface]}</h3>
        {/* Texten säger samma sak som färgen. WCAG 1.4.1. */}
        <span className={`badge badge-${status.kind}`}>{status.text}</span>
      </div>

      {usesTitle(draft.surface) && (
        <div className="field">
          <label htmlFor={`${id}-title`}>Titel</label>
          <input
            id={`${id}-title`}
            type="text"
            value={title}
            disabled={!editable}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
      )}

      <div className="field">
        <label htmlFor={`${id}-body`}>Brödtext</label>
        <textarea
          id={`${id}-body`}
          rows={6}
          value={body}
          disabled={!editable}
          aria-invalid={invalid}
          // Räknaren alltid; anmärkningarna bara när de finns. Utan dem hör
          // skärmläsaren "ogiltig" utan att få veta varför.
          aria-describedby={
            draft.issues.length > 0 ? `${id}-count ${id}-issues` : `${id}-count`
          }
          onChange={(e) => setBody(e.target.value)}
        />
      </div>

      <div className="field">
        <label htmlFor={`${id}-cta`}>Uppmaning</label>
        <input
          id={`${id}-cta`}
          type="text"
          value={callToAction}
          disabled={!editable}
          onChange={(e) => setCallToAction(e.target.value)}
        />
      </div>

      <div className="field">
        <label htmlFor={`${id}-hashtags`}>Hashtaggar</label>
        <input
          id={`${id}-hashtags`}
          type="text"
          value={hashtags}
          disabled={!editable}
          onChange={(e) => setHashtags(e.target.value)}
        />
      </div>

      {/*
        Räknaren har INGEN live-semantik. Den läses upp en gång, när fokus landar i
        textarean, via aria-describedby. Låg man den i en live-region skulle den tala
        vid varje tangenttryck.
      */}
      <p id={`${id}-count`} className={over ? 'char-count over' : 'char-count'}>
        {used} / {max} tecken i captionen
        {over && ` — ${used - max} för många`}
      </p>

      {/*
        Separat artig annonsör med en STABIL sträng utan siffra. Talar en gång vid
        tröskelpasseringen; fortsätter man skriva ändras texten inte och den tiger.
      */}
      <span role="status" className="sr-only">
        {over ? `Utkastet för ${SURFACE_LABELS[draft.surface]} överskrider teckengränsen.` : ''}
      </span>

      {draft.issues.length > 0 && (
        <>
          <p className="muted small">Anmärkningar på den senast sparade texten:</p>
          <ul className="issue-list" id={`${id}-issues`}>
            {draft.issues.map((issue) => (
              <li key={issue.message} className="muted small">
                <span className={`badge badge-${issue.severity === 'Blocking' ? 'error' : 'warn'}`}>
                  {issue.severity === 'Blocking' ? 'Blockerande' : 'Varning'}
                </span>{' '}
                {issue.message}
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="draft-actions">
        {editable ? (
          <>
            <button
              type="button"
              className="primary draft-save"
              disabled={!dirty || saving}
              onClick={() => void save()}
            >
              {saving ? 'Sparar…' : 'Spara'}
            </button>
            {dirty && (
              <button type="button" className="link draft-save" onClick={reset}>
                Återställ
              </button>
            )}
            {draft.surface === 'TikTok' && (
              <button
                type="button"
                className="primary draft-save"
                // Publicerar den SPARADE texten. Osparade ändringar skulle tyst
                // publiceras i fel version — lås knappen tills de sparats.
                disabled={dirty || !draft.canPublish}
                aria-describedby={dirty ? `${id}-publish-hint` : undefined}
                onClick={() => setPublishing(true)}
              >
                Publicera till TikTok
              </button>
            )}
          </>
        ) : (
          <p className="muted small">
            Utkastet är{' '}
            {draft.status === 'Approved'
              ? 'godkänt'
              : draft.status === 'Scheduled'
                ? 'skickat till publicering'
                : draft.status === 'Published'
                  ? 'publicerat'
                  : 'låst'}{' '}
            och kan inte ändras.
          </p>
        )}
        <span className="muted small">
          {draft.modelUsed} · ändrad {new Date(draft.updatedAt).toLocaleString('sv-SE')}
        </span>
      </div>

      {dirty && editable && draft.surface === 'TikTok' && (
        <p id={`${id}-publish-hint`} className="muted small">
          Spara ändringarna innan du publicerar.
        </p>
      )}

      {publishing && (
        <TikTokPublishDialog
          draft={draft}
          onClose={() => setPublishing(false)}
          onPublished={onPublished}
        />
      )}
    </article>
  )
}
